import { useEffect, useRef, useState } from 'react';

import { ApiErrorData, ApiResponse } from 'models/ApiResponse';
import {
  AdminCityRow,
  AdminRegion,
  AdminUniversity,
  LeadPaymentCatalogEntry,
  LeadProfileCatalogEntry,
} from 'models/Lead';

import { LeadService } from 'services/LeadService';

export type LeadCatalog = 'universities' | 'regions' | 'cities';

export type LeadCatalogErrorHandler = (
  catalog: LeadCatalog,
  error: ApiErrorData
) => void;

export interface LeadCatalogs {
  universities: AdminUniversity[];
  universitiesLoading: boolean;
  regions: AdminRegion[];
  regionsLoading: boolean;
  cities: AdminCityRow[];
  citiesLoading: boolean;
}

interface CachedCatalog<T> {
  data: T[];
  loading: boolean;
}

/**
 * Loads one catalog identified by `key`, keeping every catalog already fetched
 * in an in-memory cache.
 *
 * The cache is what makes going back to a previously visited key free: the
 * filters panel follows the draft country while it is open and snaps back to the
 * applied one when it closes, so the same catalog is requested over and over
 * otherwise. These catalogs are near-static, and the cache lives only as long as
 * the component, so remounting is a natural invalidation point.
 *
 * A `null` key means there is nothing to load and clears the current data.
 */
function useCachedCatalog<T>(
  key: string | null,
  load: () => Promise<ApiResponse<T[] | ApiErrorData>>,
  enabled: boolean,
  onError: (error: ApiErrorData) => void
): CachedCatalog<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const cacheRef = useRef<Map<string, T[]>>(new Map());
  // Clave del catalogo actualmente aplicado al estado ('' = ninguno).
  const loadedKeyRef = useRef<string>('');
  // Secuencia anti-stale: al encadenar pais -> region -> ciudad puede haber dos
  // cargas solapadas y la vieja no debe pisar a la nueva.
  const seqRef = useRef<number>(0);

  // `load` y `onError` suelen llegar como funciones inline: se guardan en refs
  // para que su identidad no dispare el efecto. La identidad del request vive
  // en `key`, que si es la unica dependencia real.
  const loadRef = useRef(load);
  loadRef.current = load;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!enabled) return;

    // Cualquier salida temprana invalida la carga en vuelo; como esa carga ya no
    // va a apagar su propio loading, se apaga aca.
    if (!key) {
      seqRef.current += 1;
      setLoading(false);
      if (loadedKeyRef.current !== '') {
        loadedKeyRef.current = '';
        setData([]);
      }
      return;
    }

    if (key === loadedKeyRef.current) return;

    const cached = cacheRef.current.get(key);
    if (cached) {
      seqRef.current += 1;
      setLoading(false);
      loadedKeyRef.current = key;
      setData(cached);
      return;
    }

    const seq = ++seqRef.current;
    setLoading(true);

    loadRef.current().then((response) => {
      if (seq !== seqRef.current) return;

      if (response.success) {
        const rows = (response.data as T[]) || [];
        cacheRef.current.set(key, rows);
        loadedKeyRef.current = key;
        setData(rows);
      } else {
        onErrorRef.current(response.data as ApiErrorData);
      }
      setLoading(false);
    });
  }, [key, enabled]);

  return { data, loading };
}

/**
 * Loads the country-scoped catalogs used by the lead filters (universities,
 * regions) plus the region-scoped city catalog, keeping the country -> region ->
 * city chain consistent.
 *
 * Cities are only fetched when a region is selected: without one the selector is
 * disabled, so there is no national catalog to load.
 *
 * The hook never renders UI. Failures are reported through `onError` so each
 * consumer keeps ownership of its own message instance and translations.
 */
export const useLeadCatalogs = (
  country: string,
  regionId: string | undefined,
  enabled: boolean,
  onError: LeadCatalogErrorHandler
): LeadCatalogs => {
  const countryKey = country || null;
  // La ciudad depende del par pais + region: ambos forman su identidad.
  const cityKey = regionId ? `${country}|${regionId}` : null;

  const universities = useCachedCatalog<AdminUniversity>(
    countryKey,
    () => LeadService.getUniversities(country),
    enabled,
    (error) => onError('universities', error)
  );

  const regions = useCachedCatalog<AdminRegion>(
    countryKey,
    () => LeadService.getRegions(country),
    enabled,
    (error) => onError('regions', error)
  );

  const cities = useCachedCatalog<AdminCityRow>(
    cityKey,
    () => LeadService.getCities(country, regionId as string),
    enabled,
    (error) => onError('cities', error)
  );

  return {
    universities: universities.data,
    universitiesLoading: universities.loading,
    regions: regions.data,
    regionsLoading: regions.loading,
    cities: cities.data,
    citiesLoading: cities.loading,
  };
};

export type LeadProfileCatalog =
  | 'nationalities'
  | 'genders'
  | 'marital_statuses';

export type LeadProfileCatalogErrorHandler = (
  catalog: LeadProfileCatalog,
  error: ApiErrorData
) => void;

export interface LeadProfileCatalogs {
  nationalities: LeadProfileCatalogEntry[];
  nationalitiesLoading: boolean;
  genders: LeadProfileCatalogEntry[];
  gendersLoading: boolean;
  maritalStatuses: LeadProfileCatalogEntry[];
  maritalStatusesLoading: boolean;
}

// Las nacionalidades no dependen del pais: una sola clave fija.
const NATIONALITIES_KEY = 'global';

/**
 * Loads the lead profile catalogs: nationalities (global) plus genders and
 * marital statuses (per country). Same cache and error contract as
 * `useLeadCatalogs`, kept apart so the screens that do not show these fields
 * do not request them.
 */
export const useLeadProfileCatalogs = (
  country: string,
  enabled: boolean,
  onError: LeadProfileCatalogErrorHandler
): LeadProfileCatalogs => {
  const countryKey = country || null;

  const nationalities = useCachedCatalog<LeadProfileCatalogEntry>(
    NATIONALITIES_KEY,
    () => LeadService.getNationalities(),
    enabled,
    (error) => onError('nationalities', error)
  );

  const genders = useCachedCatalog<LeadProfileCatalogEntry>(
    countryKey,
    () => LeadService.getGenders(country),
    enabled,
    (error) => onError('genders', error)
  );

  const maritalStatuses = useCachedCatalog<LeadProfileCatalogEntry>(
    countryKey,
    () => LeadService.getMaritalStatuses(country),
    enabled,
    (error) => onError('marital_statuses', error)
  );

  return {
    nationalities: nationalities.data,
    nationalitiesLoading: nationalities.loading,
    genders: genders.data,
    gendersLoading: genders.loading,
    maritalStatuses: maritalStatuses.data,
    maritalStatusesLoading: maritalStatuses.loading,
  };
};

export type LeadPaymentCatalog =
  | 'banks'
  | 'bank_account_types'
  | 'pension_funds'
  | 'health_providers';

export type LeadPaymentCatalogErrorHandler = (
  catalog: LeadPaymentCatalog,
  error: ApiErrorData
) => void;

export interface LeadPaymentCatalogs {
  banks: LeadPaymentCatalogEntry[];
  banksLoading: boolean;
  bankAccountTypes: LeadPaymentCatalogEntry[];
  bankAccountTypesLoading: boolean;
  pensionFunds: LeadPaymentCatalogEntry[];
  pensionFundsLoading: boolean;
  healthProviders: LeadPaymentCatalogEntry[];
  healthProvidersLoading: boolean;
}

/**
 * Loads the payment catalogs ("Datos de pago") of a country. Same cache and
 * error contract as `useLeadProfileCatalogs`. An empty country loads nothing:
 * pass it only for leads that have payment data.
 */
export const useLeadPaymentCatalogs = (
  country: string,
  enabled: boolean,
  onError: LeadPaymentCatalogErrorHandler
): LeadPaymentCatalogs => {
  const countryKey = country || null;

  const banks = useCachedCatalog<LeadPaymentCatalogEntry>(
    countryKey,
    () => LeadService.getBanks(country),
    enabled,
    (error) => onError('banks', error)
  );

  const bankAccountTypes = useCachedCatalog<LeadPaymentCatalogEntry>(
    countryKey,
    () => LeadService.getBankAccountTypes(country),
    enabled,
    (error) => onError('bank_account_types', error)
  );

  const pensionFunds = useCachedCatalog<LeadPaymentCatalogEntry>(
    countryKey,
    () => LeadService.getPensionFunds(country),
    enabled,
    (error) => onError('pension_funds', error)
  );

  const healthProviders = useCachedCatalog<LeadPaymentCatalogEntry>(
    countryKey,
    () => LeadService.getHealthProviders(country),
    enabled,
    (error) => onError('health_providers', error)
  );

  return {
    banks: banks.data,
    banksLoading: banks.loading,
    bankAccountTypes: bankAccountTypes.data,
    bankAccountTypesLoading: bankAccountTypes.loading,
    pensionFunds: pensionFunds.data,
    pensionFundsLoading: pensionFunds.loading,
    healthProviders: healthProviders.data,
    healthProvidersLoading: healthProviders.loading,
  };
};
